import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Stack, Typography } from '@mui/material';
import AppDialog from '../common/AppDialog';
import { Add, CategoryOutlined, DeleteOutline, EditOutlined } from '@mui/icons-material';
import { Category } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiClient } from '../../services/api';
import { errorMessage } from '../../utils/errorMessage';
import RowActionsButton from '../common/RowActionsButton';

interface CategoryManagementDialogProps {
  open: boolean;
  onClose: () => void;
  onAddCategory: () => void;
  onEditCategory: (category: Category) => void;
  onDeleteCategory: (category: Category) => void;
  refreshTrigger?: number;
}

const CategoryRow: React.FC<{ category: Category; onEdit: (category: Category) => void; onDelete: (category: Category) => void }> = ({ category, onEdit, onDelete }) => {
  const { t } = useLanguage();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, borderRadius: 1.5 }}>
    <Box sx={{ width: 38, height: 38, borderRadius: 1, display: 'grid', placeItems: 'center', bgcolor: category.color ? `${category.color}22` : 'action.selected', color: category.color || 'primary.main', flexShrink: 0 }}><CategoryOutlined fontSize="small" /></Box>
    <Box sx={{ minWidth: 0, flex: 1 }}><Typography fontWeight={700}>{category.name}</Typography><Typography variant="body2" color="text.secondary" noWrap>{category.description || t('category.noDescription')}</Typography></Box>
    <RowActionsButton aria-label={`${t('category.actions')}: ${category.name}`} aria-haspopup="menu" onClick={event => setAnchor(event.currentTarget)} />
    <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
      <MenuItem onClick={() => { setAnchor(null); onEdit(category); }}><ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon><ListItemText>{t('common.edit')}</ListItemText></MenuItem>
      <MenuItem onClick={() => { setAnchor(null); onDelete(category); }} sx={{ color: 'error.main' }}><ListItemIcon><DeleteOutline fontSize="small" color="error" /></ListItemIcon><ListItemText>{t('common.delete')}</ListItemText></MenuItem>
    </Menu>
  </Paper>;
};

const CategoryManagementDialog: React.FC<CategoryManagementDialogProps> = ({ open, onClose, onAddCategory, onEditCategory, onDeleteCategory, refreshTrigger }) => {
  const { t, language } = useLanguage();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError(null);
    apiClient.getCategories().then(data => { if (active) setCategories(data); }).catch(err => { if (active) setError(errorMessage(err, t('loading.failedToLoad'))); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, refreshTrigger, language, t]);

  return <AppDialog presentation="compact" open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>{t('category.manage')}</DialogTitle>
    <DialogContent dividers sx={{ p: 2.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('category.managementHint')}</Typography>
      <Button variant="contained" startIcon={<Add />} onClick={onAddCategory} fullWidth sx={{ mb: 2 }}>{t('category.add')}</Button>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box> : categories.length === 0 ? <Box sx={{ textAlign: 'center', py: 5 }}><CategoryOutlined sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} /><Typography color="text.secondary">{t('category.noCategories')}</Typography></Box> : <Stack spacing={1}>{categories.map(category => <CategoryRow key={category.id} category={category} onEdit={onEditCategory} onDelete={onDeleteCategory} />)}</Stack>}
    </DialogContent>
    <DialogActions sx={{ px: 2.5, py: 1.5 }}><Button onClick={onClose}>{t('common.close')}</Button></DialogActions>
  </AppDialog>;
};

export default CategoryManagementDialog;
